// @flow
import React from 'react'
import { connect } from 'react-redux'
import I18n from 'i18n-react'
import moment from 'moment'
import { humanizer } from 'devx-js-utilities'

import csLanguage from '../Translations/cs.json'

// components
import Loader from '../Components/Loader'
import String from '../Components/fields/String'
import Number from '../Components/fields/Number'

// redux
import CarActions from '../Redux/CarRedux'
import GeneralActions from '../Redux/GeneralRedux'
import ValidationActions from '../Redux/ValidationRedux'

// types
import type { GlobalState } from '../store/reducers'
import type { ErrorType } from '../Types/ErrorTypes'
import FormBackOffice from '../Components/FormBackOffice'

type CarRequestType = {
  modalRZ: string,
  modalVIN: string,
  modalPhone: string,
  modalMileage: string,
}

type StateProps = {
  autoFinish: boolean,
  carInfo: Object,
  carInfoRequest: (CarRequestType, boolean, boolean, string) => void,
  carValuationRequest: (string) => void,
  config: Array<string>,
  data: CarRequestType & { verificationCode: string },
  errors: Array<ErrorType>,
  fetching: number,
  identificated: any,
  isBackOffice?: boolean,
  isCarValuationView?: boolean,
  isWindscreenView?: boolean,
  onChangeValidationError: (string, any) => void,
  onChangeValue: (string, string) => void,
  onCreate?: (Function, Function) => void,
  onErrorsReset: () => void,
  onFinish: (finished: boolean, Object, ?string) => void,
  onSetVerificationCode?: (string) => void,
  remoteValidationErrors: Object,
  renderBottomContent?: () => Element,
  setData: (any) => void,
  showMileagePush?: boolean,
  showPhoneValidation: boolean,
  validationErrors: Object,
  verificationSmsRequest: (string) => void,
  vinFromRzRequest: (string, boolean, boolean, string) => void,
  windscreenPrices: Object,
}

type ScreenState = {
  pushIsPristine: boolean,
}

class RootContainer extends React.Component<StateProps, ScreenState> {
  modalMileage = React.createRef()
  modalPhone = React.createRef()
  modalRz = React.createRef()
  modalVin = React.createRef()
  verificationCode = React.createRef()

  static defaultProps = {
    isBackOffice: false,
    isCarValuationView: false,
  }

  constructor(props) {
    super(props)
    I18n.setTexts({
      ...I18n.texts,
      ...csLanguage,
    })
    this.state = {
      pushIsPristine: true,
    }

    console.debug('constructor', this.modalRz, this.modalVin);
  }

  componentDidMount() {
    this.props.onCreate && this.props.onCreate(this.props.setData, this.handleSetError)
  }

  componentDidUpdate(prevProps: StateProps) {
    if (this.props.autoFinish) {
      this.props.setAutoFinish(false)
      this.handleSelect(this.props.carInfo)
    }
    if (this.verificationCode && this.verificationCode.current) {
      this.verificationCode.current.ref.current.focus()
    }
    if (!this.props.data.verificationCode && prevProps.data.verificationCode) {
      if (this.props.onSetVerificationCode && typeof this.props.onSetVerificationCode === 'function') {
        this.props.onSetVerificationCode('')
      }
    }
  }

  handleChange = (key: string, value: string) => {
    this.props.onChangeValue(key, value)
  }

  handleOnBlur = (key: string, value: any) => {
    if (key !== 'verificationCode') {
      this.props.onErrorsReset()
    }
    switch (key) {
      case 'modalRZ':
        this.modalVin.current.validate(this.props.data.modalVIN)
        break
      case 'modalVIN':
        this.modalRz.current.validate(this.props.data.modalRZ)
        break
    }
    if (this.props.onFinish) {
      this.props.onFinish(false, {
        modalPhone: this.props.isBackOffice ? '' : this.props.data.modalPhone,
        modalRZ: this.props.data.modalRZ,
        modalMileage: this.props.data.modalMileage,
        modalVIN: this.props.data.modalVIN,
        [key]: value,
      })
    }
  }

  handleChangeValidationError = (key: string, value: any) => {
    this.props.onChangeValidationError(key, value)
  }

  handleSendValidationCode = () => {
    this.props.verificationSmsRequest(this.props.data.modalPhone)
  }

  handleGetPrice = (vehicle: any) => {
    let registrationDate = this.props.data.firstRegistered
    if (vehicle.registration.firstRegistrationDateEU && vehicle.registration.firstRegistrationDateEU.length > 0) {
      registrationDate = moment(vehicle.registration.firstRegistrationDateEU).format('YYYY-MM-DD')
    }
    if (registrationDate && registrationDate.length > 0) {
      const data = {
        vehicleId: vehicle.administrativeData.id,
        registrationDate,
      }
      if (this.props.data.modalMileage) {
        data.mileage = this.props.data.modalMileage
      }
      let accessories = []
      if (vehicle.accessories && Array.isArray(vehicle.accessories)) {
        accessories = vehicle.accessories
          .filter((accessory) => accessory.selected && accessory.price && accessory.price.withVAT)
          .map((accessory) => accessory.code)
      }
      if (this.props.onGetPrice) {
        this.props.onGetPrice()
      }
      this.props.carValuationRequest(data, accessories)
    } else {
      this.props.onChangeRequestFailure('CAR_INFO_FIRST_TIME_REGISTRATION_FAILURE')
    }
  }

  handleSelect = (vehicle: any, selected: string, withVat: boolean = false) => {
    if (this.props.onFinish) {
      this.props.onFinish(
        true,
        {
          modalMileage: this.props.data.modalMileage,
          modalPhone: this.props.isBackOffice ? '' : this.props.data.modalPhone,
          modalRZ: this.props.data.modalRZ,
          modalVIN: this.props.data.modalVIN,
          technicalCardNo: this.props.data.technicalCardNo,
          ...vehicle,
        },
        selected,
        withVat,
      )
    }
  }

  handleSelectWindscreenPrice = (value: number) => {
    if (this.props.onFinish) {
      this.props.onFinish(true, {
        value,
        modalPhone: this.props.isBackOffice ? '' : this.props.data.modalPhone,
        modalRZ: this.props.data.modalRZ,
        modalMileage: this.props.data.modalMileage || '',
        modalVIN: this.props.data.modalVIN,
      })
    }
  }

  handleSetError = (errors: Object, pushIsPristine: boolean) => {
    this.props.onSetErrors(errors)
    this.setState({ pushIsPristine })
  }

  handleSubmit = (e) => {
    e.preventDefault()
    const { modalPhone, modalRZ, modalVIN, modalMileage, verificationCode } = this.props.data

    if (!this.props.isBackOffice && !verificationCode) {
      if (
        this.modalPhone.current.validate(modalPhone) &&
        (this.modalRz.current.validate(modalRZ) &&
          this.modalVin.current.validate(modalVIN) &&
          (!this.props.isCarValuationView || this.modalMileage.current.validate(modalMileage)))
      ) {
        this.props.onErrorsReset()
        this.handleSendValidationCode()
      }
    } else if (
      (this.verificationCode &&
        this.verificationCode.current &&
        this.verificationCode.current.validate(verificationCode)) ||
      (this.modalRz.current.validate(modalRZ) &&
        this.modalVin.current.validate(modalVIN) &&
        (!this.props.isCarValuationView || this.modalMileage.current.validate(modalMileage)))
    ) {
      if (this.props.onSetVerificationCode && typeof this.props.onSetVerificationCode === 'function') {
        this.props.onSetVerificationCode(verificationCode)
      }
      this.props.onErrorsReset()
      if (modalRZ && !modalVIN) {
        this.props.vinFromRzRequest(
          modalRZ.replace(' ', ''),
          !this.props.isBackOffice && !this.props.isCarValuationView && !this.props.isWindscreenView,
          this.props.isCarValuationView ? 'carValuation' : this.props.isWindscreenView ? 'windScreen' : 'vehicleInfo',
        )
      } else if (modalVIN) {
        this.props.carInfoRequest(
          modalVIN.replace(' ', ''),
          !this.props.isBackOffice && !this.props.isCarValuationView && !this.props.isWindscreenView,
          this.props.isCarValuationView ? 'carValuation' : this.props.isWindscreenView ? 'windScreen' : 'vehicleInfo',
        )
      }
    } else if (this.props.onFinish && !this.props.showMileagePush) {
      this.props.onFinish(false, {
        valid: {
          modalMileage: !this.props.isCarValuationView || this.modalMileage.current.validate(modalMileage),
        },
      })
    }
    this.setState({ pushIsPristine: false })
  }

  renderUniForField = (field: string) => {
    switch (field) {
      case 'capacity':
        return (
          <span>
            cm<sup>3</sup>
          </span>
        )
      case 'power':
        return <span>kW</span>
      case 'totalWeight':
        return <span>kg</span>
      default:
        return ''
    }
  }

  renderResultValue = (view: string, field: string, value: string) => {
    if (!value && this.props.data[field]) {
      value = this.props.data[field]
    } else if (!value) {
      return null
    }

    if (field === 'firstTimeRegistration') {
      value = moment(value).format('DD.MM.YYYY')
    } else if (field === 'fuelType') {
      value = I18n.translate('vehicleInfo.fuelTypes', { context: value })
    }
    return (
      <div className="col-12" key={field}>
        <strong>{I18n.translate(`${view}.carInfo.${field}`)}</strong>&nbsp;{value}&nbsp;{this.renderUniForField(field)}
      </div>
    )
  }

  render() {
    const { data: { verificationCode }, isBackOffice } = this.props
    const combinedValidationErrors = Object.assign(
      { ...this.props.validationErrors },
      { ...this.props.remoteValidationErrors },
    )
    const view = this.props.isCarValuationView
      ? 'carValuation'
      : this.props.isWindscreenView
      ? 'windScreen'
      : 'vehicleInfo'

    let results = null
    if (this.props.carInfo && this.props.carInfo.vin) {
      results = (
        <div className="my-5">
          <div className="card card-with-edit with-default-bg flex-row px-4 pt-3 pb-2">
            <div className="row">
              {this.props.config && this.props.config.carInfoFields
                ? this.props.config.carInfoFields.map((field) =>
                    this.renderResultValue(view, field, this.props.carInfo[field]),
                  )
                : Object.entries(this.props.carInfo).map((field) =>
                    this.renderResultValue(view, field[0], this.props.carInfo[field[1]]),
                  )}
            </div>
            <a className="edit-btn text-nowrap" onClick={() => this.handleSelect(this.props.carInfo)}>
              <strong>
                <u>{I18n.translate(`vehicleInfoGeneral.fillInfo`)}</u>
              </strong>
            </a>
          </div>
        </div>
      )
      if (!isBackOffice) {
        return results
      }
    } else if (this.props.identificated && Object.keys(this.props.identificated).length > 0) {
      results = Object.values(this.props.identificated).map((vehicle) => (
        <div className="my-4" key={vehicle.administrativeData.id}>
          <div className="card card-with-edit with-default-bg px-4 pt-3 pb-2">
            <div className="row">
              <div className="col-sm-6 col-md-5 text-md-left">
                <p>
                  <strong>
                    {I18n.translate(`${view}.carDescription`)} {this.props.data.modalVIN}
                  </strong>
                </p>
                <p>
                  {vehicle.identification.brand.name} {vehicle.identification.model.name}{' '}
                  {vehicle.identification.version.versionName}
                  <br />
                  {I18n.translate(`${view}.equipmentLevel`)}:&nbsp;
                  {vehicle.identification.equipmentLevel
                    ? vehicle.identification.equipmentLevel
                    : I18n.translate(`${view}.basicEquipment`)}
                </p>
              </div>
              <div className="col-sm-6 col-md-2">
                <p>
                  <strong>{I18n.translate(`${view}.saledFrom`)}</strong>
                </p>
                <p>{moment(vehicle.identification.version.versionOnMarketIntroduced).format('DD.MM.YYYY')}</p>
              </div>
              <div className="col-sm-6 col-md-2">
                <p>
                  <strong>{I18n.translate(`${view}.saledTo`)}</strong>
                </p>
                {moment(vehicle.identification.version.versionOnMarketConcluded).diff(moment()) > 0 ? (
                  <p>{I18n.translate(`${view}.stillForSale`)}</p>
                ) : (
                  <p>{moment(vehicle.identification.version.versionOnMarketConcluded).format('DD.MM.YYYY')}</p>
                )}
              </div>
              <div className="col-sm-6 col-md-3">
                <p>
                  <strong>{I18n.translate(`${view}.priceNewDph`)}</strong>
                </p>
                <p>
                  {humanizer.csNumber(vehicle.identification.version.price.withVAT)} Kč
                  <a
                    className="ml-md-2"
                    onClick={() => {
                      this.handleGetPrice(vehicle)
                    }}
                  >
                    {I18n.translate(`${view}.getPrice`)}
                  </a>
                </p>
              </div>
            </div>
            <div className="row">
              {vehicle.prices
                ? [
                    <div className="col-sm-3" key="title">
                      <p>{I18n.translate(`${view}.defaultPrice`)}</p>
                    </div>,
                    <div className="col-sm-4" key="res">
                      <p>
                        {I18n.translate(`${view}.priceWithoutVat`, {
                          context: humanizer.csNumber(Math.round(vehicle.prices.vehicleMarketPrice)),
                        })}
                        &nbsp;
                        <a
                          onClick={() => {
                            this.handleSelect(vehicle, 'vehicleMarketPrice')
                          }}
                        >
                          {I18n.translate('vehicleInfoGeneral.pick')}
                        </a>
                      </p>
                    </div>,
                  ]
                : null}
              <div className="col-sm-5">
                {vehicle.prices ? (
                  <p>
                    {I18n.translate(`${view}.priceWithVat`, {
                      context: humanizer.csNumber(Math.round(vehicle.prices.vehicleMarketPrice * 1.21)),
                    })}
                    &nbsp;
                    <a
                      onClick={() => {
                        this.handleSelect(vehicle, 'vehicleMarketPrice', true)
                      }}
                    >
                      {I18n.translate('vehicleInfoGeneral.pick')}
                    </a>
                  </p>
                ) : null}
              </div>
            </div>
            <div className="row">
              <div className="col-sm-3" />
              <div className="col-sm-4" key="res">
                {vehicle.prices ? (
                  <p>
                    {I18n.translate(`${view}.priceWithoutVat`, {
                      context: humanizer.csNumber(Math.round(vehicle.prices.baseVehicleNewPrice)),
                    })}
                    &nbsp;
                    <a
                      onClick={() => {
                        this.handleSelect(vehicle, 'baseVehicleNewPrice')
                      }}
                    >
                      {I18n.translate('vehicleInfoGeneral.pick')}
                    </a>
                  </p>
                ) : null}
              </div>
              <div className="col-sm-5">
                {vehicle.prices ? (
                  <p>
                    {I18n.translate(`${view}.priceWithVat`, {
                      context: humanizer.csNumber(Math.round(vehicle.prices.baseVehicleNewPrice * 1.21)),
                    })}
                    &nbsp;
                    <a
                      onClick={() => {
                        this.handleSelect(vehicle, 'baseVehicleNewPrice', true)
                      }}
                    >
                      {I18n.translate('vehicleInfoGeneral.pick')}
                    </a>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ))
      results = (
        <div>
          {this.props.errors &&
            this.props.errors.map((error) =>
              error.error === 'CAR_INFO_FIRST_TIME_REGISTRATION_FAILURE' && this.props.isBackOffice ? null : (
                <div className="alert alert-danger" key={error.error} role="alert">
                  <span>{I18n.translate(`vehicleInfoGeneral.validationErrors.${error.error}`)}</span>
                </div>
              ),
            )}
          {results}
        </div>
      )
      if (!this.props.isBackOffice) {
        return results
      }
    } else if (this.props.windscreenPrices) {
      results = (
        <div className="my-5">
          <div className="card card-with-edit with-default-bg flex-row px-4 pt-3 pb-2">
            <div className="row w-100">
              <div className="col-sm-6 col-md-6">
                <p>
                  <strong>{I18n.translate(`${view}.prices.original`)}</strong>
                  {humanizer.csNumber(this.props.windscreenPrices.priceOEM.median)}&nbsp;Kč&nbsp;
                  <a
                    onClick={() => {
                      this.handleSelectWindscreenPrice(this.props.windscreenPrices.priceOEM.median)
                    }}
                  >
                    {I18n.translate('vehicleInfoGeneral.choose')}
                  </a>
                </p>
              </div>
              <div className="col-sm-6 col-md-6">
                <p>
                  <strong>{I18n.translate(`${view}.prices.cheap`)}</strong>
                  {humanizer.csNumber(this.props.windscreenPrices.priceAftermarket.median)}&nbsp;Kč&nbsp;
                  <a
                    onClick={() => {
                      this.handleSelectWindscreenPrice(this.props.windscreenPrices.priceAftermarket.median)
                    }}
                  >
                    {I18n.translate('vehicleInfoGeneral.choose')}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )
      if (!this.props.isBackOffice) {
        return results
      }
    }

    if (this.props.showPhoneValidation) {
      return (
        <form className="p-3 text-center position-relative" onSubmit={this.handleSubmit}>
          {this.props.errors &&
            this.props.errors.map((error) =>
              error.error === 'CAR_INFO_FIRST_TIME_REGISTRATION_FAILURE' ? null : (
                <div className="alert alert-danger" key={error.error} role="alert">
                  <span>{I18n.translate(`vehicleInfoGeneral.validationErrors.${error.error}`)}</span>
                </div>
              ),
            )}
          <div className="alert alert-info " role="alert">
            <span>
              <strong>{I18n.translate(`vehicleInfoGeneral.code.info`)}</strong>
            </span>
          </div>
          <div className="form-group d-flex flex-row flex-wrap flex-sm-row align-items-center">
            <div className="col-12 col-lg-4 order-0 order-sm-0 text-sm-left text-lg-right">
              <label className="mb-0 font-weight-bold" htmlFor="finalDestination">
                {I18n.translate(`vehicleInfoGeneral.fields.code.label`)}
                <span className="text-danger">*</span>
              </label>
            </div>
            <div className="col-12 col-lg-5 order-3 order-sm-0 my-2 pr-md-0">
              <String
                className="form-control mw-auto"
                id="verificationCode"
                isPristine={this.state.pushIsPristine}
                name="verificationCode"
                onBlur={this.handleOnBlur}
                onChange={this.handleChange}
                onErrorChange={this.handleChangeValidationError}
                placeholder={I18n.translate(`vehicleInfoGeneral.fields.code.placeholder`)}
                ref={this.verificationCode}
                required
                value={this.props.data.verificationCode}
              />
            </div>
          </div>
          <div className="my-2">
            <i>{I18n.translate(`vehicleInfoGeneral.code.info`)}</i>
            <a className="font-weight-bold text-dark" onClick={this.handleSendValidationCode}>
              <u>{I18n.translate(`vehicleInfoGeneral.code.reSend`)}</u>
            </a>
          </div>
          <div className="row">
            <div className="col-12 col-md-8 offset-sm-2 mt-3">
              <button
                className="btn btn-dark"
                onClick={(e) => {
                  this.handleSubmit(e)
                }}
                type="button"
              >
                {I18n.translate(`vehicleInfoGeneral.code.use`)}
              </button>
            </div>
          </div>
          <Loader active={!!this.props.fetching} key={`${view}-loader`} />
        </form>
      )
    }

    const rzOrVinRequired =
      (this.props.validationErrors.modalRZ && Object.keys(this.props.validationErrors.modalRZ).length > 0) ||
      (this.props.validationErrors.modalVIN && Object.keys(this.props.validationErrors.modalVIN).length > 0)
    return (
      <form className="position-relative" onSubmit={this.handleSubmit}>
        {this.props.errors &&
          this.props.errors.map((error) => (
            <div className="alert alert-info" key={error.error} role="alert">
              <span>{I18n.translate(`vehicleInfoGeneral.validationErrors.${error.error}`)}</span>
            </div>
          ))}
        {!this.props.isBackOffice && (
          <div className="alert alert-info" role="alert">
            <span>{I18n.translate(`${view}.info`)}</span>
          </div>
        )}
        <FormBackOffice
          combinedValidationErrors={combinedValidationErrors}
          data={this.props.data}
          handleChange={this.handleChange}
          handleChangeValidationError={this.handleChangeValidationError}
          handleOnBlur={this.handleOnBlur}
          handleSubmit={this.handleSubmit}
          isBackOffice={isBackOffice}
          modalRz={this.modalRz}
          modalVin={this.modalVin}
          pushIsPristine={this.pushIsPristine}
          rzOrVinRequired={rzOrVinRequired}
          view={view}
        />
        {this.props.isCarValuationView && (
          <div
            className={`form-group flex-row flex-wrap flex-sm-row align-items-center ${
              this.props.isBackOffice && !this.props.showMileagePush ? 'd-none' : 'd-flex'
            }`}
            key="phone-input"
          >
            <div className="col-12 col-lg-4 order-0 order-sm-0 text-lg-right">
              <label className="mb-0 font-weight-bold" htmlFor="finalDestination">
                {I18n.translate(`vehicleInfoGeneral.fields.mileage.label`)}
                <span className="text-danger">*</span>
              </label>
            </div>
            <div className="col-12 col-lg-5 order-3 order-sm-0 my-2 pr-md-0">
              <Number
                enabled
                id="modalMileage"
                isPristine={this.state.pushIsPristine}
                max={10000000}
                min={0}
                name="modalMileage"
                onBlur={this.handleOnBlur}
                onChange={this.handleChange}
                onErrorChange={this.handleChangeValidationError}
                ref={this.modalMileage}
                required
                step={1}
                tabIndex="1"
                validationErrors={this.props.validationErrors}
                value={this.props.data.modalMileage}
              />
            </div>
          </div>
        )}
        {!this.props.isBackOffice &&
          !verificationCode && [
            <div className="form-group d-flex flex-row flex-wrap flex-sm-row align-items-center" key="phone-input">
              <div className="col-12 col-lg-4 order-0 order-sm-0 text-lg-right">
                <label className="mb-0 font-weight-bold" htmlFor="finalDestination">
                  {I18n.translate(`vehicleInfoGeneral.fields.phone.label`)}
                  <span className="text-danger">*</span>
                </label>
              </div>
              <div className="col-12 col-lg-8 order-3 order-sm-0 my-2 pr-md-0 pr-sm-0">
                <div className="row mr-1">
                  <div className="col-12 col-sm-4 order-3 order-sm-0 my-2 pr-md-0 pr-sm-0">
                    <div>
                      <div className="d-inline-block w-100">
                        <div className="form-group mb-0 d-flex flex-wrap align-items-center">
                          <String
                            className="form-control mw-auto"
                            id="modalPhone"
                            inputType="tel"
                            isPristine={this.state.pushIsPristine}
                            name="modalPhone"
                            onBlur={this.handleOnBlur}
                            onChange={this.handleChange}
                            onErrorChange={this.handleChangeValidationError}
                            placeholder={I18n.translate(`vehicleInfoGeneral.fields.phone.placeholder`)}
                            ref={this.modalPhone}
                            required
                            validationErrors={this.props.validationErrors}
                            value={this.props.data.modalPhone}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6 col-lg-5 order-3 order-sm-0 my-2 pr-md-0">
                    <div className="text-left">
                      <i>{I18n.translate(`vehicleInfoGeneral.fields.phone.info`)}</i>
                    </div>
                  </div>
                </div>
              </div>
            </div>,
            this.props.renderBottomContent && typeof this.props.renderBottomContent === 'function' ? (
              <div className="my-2" key="render-bottom-content">
                {this.props.renderBottomContent()}
              </div>
            ) : null,
          ]}
        {!this.props.isBackOffice && (
          <div className="row text-center">
            <div className="col-12 col-md-8 offset-md-2 mt-3">
              <button className="btn btn-dark" onClick={this.handleSubmit} type="submit">
                {I18n.translate(`${view}.button.load`)}
              </button>
            </div>
          </div>
        )}
        {this.props.isBackOffice && results}
        <Loader active={!!this.props.fetching} key={`${view}-loader`} />
      </form>
    )
  }
}

const mapStateToProps = (state: GlobalState) => ({
  autoFinish: state.car.autoFinish,
  carInfo: state.car.carInfo,
  data: state.car.data,
  errors: state.general.errors,
  fetching: state.general.fetching,
  identificated: state.car.identificated,
  remoteValidationErrors: state.validation.remoteValidationErrors,
  showPhoneValidation: state.car.showPhoneValidation,
  validationErrors: state.validation.validationErrors,
  windscreenPrices: state.car.windscreenPrices,
})

const mapDispatchToProps = {
  carInfoRequest: CarActions.carInfoRequest,
  carValuationRequest: CarActions.carValuationRequest,
  onChangeRequestFailure: GeneralActions.onChangeRequestFailure,
  onChangeValidationError: ValidationActions.onChangeValidationError,
  onChangeValue: CarActions.onChangeValue,
  onErrorsReset: GeneralActions.onErrorsReset,
  onSetErrors: ValidationActions.onSetErrors,
  setAutoFinish: CarActions.setAutoFinish,
  setData: CarActions.setData,
  verificationSmsRequest: CarActions.verificationSmsRequest,
  vinFromRzRequest: CarActions.vinFromRzRequest,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RootContainer)
